import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Aperture, BarChart3, Video, Users, Activity, ChevronDown, Lightbulb, TrendingUp, Rocket, LayoutDashboard, FileBarChart, Brain, Send, LineChart, Newspaper, MessageSquare, Camera, Trophy, Wand2, CalendarDays } from 'lucide-react';
import { cn } from './ui/utils';

interface AppSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  onNavigate?: () => void;
  currentPage?: string;
  onPageChange?: (page: string) => void;
  onExpandedChange?: (expanded: boolean) => void;
  /** When false, hide Messages, Brand Pulse, Discover sub-items (except Creators), Ad Studio, Evolve. */
  showCowork?: boolean;
}

const userName = 'Noemi Miki';
const _brandName = 'Luminara Beauty';
const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();

function getInitialsColor(initials: string) {
  const colors = [
    { bg: '#EDE9FE', text: '#7C3AED' },
    { bg: '#DBEAFE', text: '#2563EB' },
    { bg: '#D1FAE5', text: '#059669' },
    { bg: '#FEF3C7', text: '#D97706' },
    { bg: '#FCE7F3', text: '#DB2777' },
    { bg: '#E0E7FF', text: '#4F46E5' },
  ];
  const index = initials.charCodeAt(0) % colors.length;
  return colors[index];
}

// Page slug helper
function toPageSlug(label: string): string {
  const map: Record<string, string> = {
    'Home': 'dashboard',
    'Messages': 'messages',
    'Brand Pulse': 'brand-pulse',
    'Content Hub': 'content-hub',
    'Creators': 'creators',
    'Audience Insights': 'audience-insights',
    'Trends': 'trends',
    'News Monitor': 'news-monitor',
    'Ad Studio': 'ad-studios',
    'Video Ad Studio': 'video-ad-studio',
    'Audio Dubbing': 'audio-dubbing',
    'Create Campaign': 'campaigns',
    'All Campaigns': 'see-campaigns',
    Calendar: 'campaign-calendar',
    'Performance': 'performance',
    'Brand Lift': 'brand-lift',
    'Marketing Mix Model': 'marketing-mix-model-mmm',
    'Awards': 'trophies',
  };
  return map[label] || label.toLowerCase().replace(/\s+/g, '-');
}

/** Temporarily hidden Discover sub-items (UI only — data kept in section defs). */
const HIDDEN_DISCOVER_ITEM_LABELS = new Set(['Audience Insights', 'Trends', 'News Monitor']);

// Section definitions
const sections = [
  {
    id: 'Discover',
    label: 'Discover',
    icon: Rocket,
    items: [
      { label: 'Creators', icon: Users },
      { label: 'Audience Insights', icon: Lightbulb },
      { label: 'Trends', icon: TrendingUp },
      { label: 'News Monitor', icon: Newspaper },
    ],
  },
  {
    id: 'Create',
    label: 'Ad Studio',
    icon: Aperture,
    items: [
      { label: 'Ad Studio', icon: Wand2 },
      { label: 'Video Ad Studio', icon: Video },
      { label: 'Audio Dubbing', icon: Video },
    ],
  },
  {
    id: 'Campaigns',
    label: 'Campaigns',
    icon: Send,
    items: [
      { label: 'All Campaigns', icon: BarChart3 },
      { label: 'Calendar', icon: CalendarDays },
      { label: 'Create Campaign', icon: Rocket },
    ],
  },
  {
    id: 'Evolve',
    label: 'Evolve',
    icon: LineChart,
    items: [
      { label: 'Performance', icon: LayoutDashboard },
      { label: 'Brand Lift', icon: FileBarChart },
      { label: 'Marketing Mix Model', icon: Brain },
      { label: 'Awards', icon: Trophy },
    ],
  },
];

const quickNavItems = [
  { icon: Home, label: 'Home', page: 'dashboard' },
  { icon: MessageSquare, label: 'Messages', page: 'messages' },
  { icon: Activity, label: 'Brand Pulse', page: 'brand-pulse' },
  { icon: Video, label: 'Content Hub', page: 'content-hub' },
];

function buildVisibleQuickNav(showCowork: boolean) {
  if (showCowork) {
    return quickNavItems;
  }
  return quickNavItems.filter((item) => item.page !== 'messages' && item.page !== 'brand-pulse');
}

function buildVisibleSections(showCowork: boolean) {
  if (showCowork) {
    return sections;
  }
  return sections
    .filter((sec) => sec.id !== 'Create' && sec.id !== 'Evolve')
    .map((sec) =>
      sec.id === 'Discover'
        ? { ...sec, items: sec.items.filter((item) => item.label === 'Creators') }
        : sec,
    );
}

/** Map creator-embed sub-routes so Discover → Creators stays highlighted. */
function navPageForCreatorsSidebar(page: string): string {
  if (page === 'search-results' || page === 'creator-profile' || page === 'discovery-creatorlist') {
    return 'creators';
  }
  if (page === 'campaign-report-embed') {
    return 'see-campaigns';
  }
  return page;
}

// Which section a page belongs to (within the given section list)
function getActiveSectionId(page: string, sectionList: typeof sections): string | null {
  const p = navPageForCreatorsSidebar(page);
  for (const section of sectionList) {
    if (section.items.some((item) => toPageSlug(item.label) === p)) {
      return section.id;
    }
  }
  return null;
}

export function AppSidebar({
  isCollapsed: _isCollapsed,
  onToggle: _onToggle,
  isMobile = false,
  onNavigate,
  currentPage,
  onPageChange,
  onExpandedChange,
  showCowork = false,
}: AppSidebarProps) {
  const { t } = useTranslation();
  const visibleSections = useMemo(() => buildVisibleSections(showCowork), [showCowork]);
  const visibleQuickNav = useMemo(() => buildVisibleQuickNav(showCowork), [showCowork]);

  const [expandedSections, setExpandedSections] = useState<string[]>(() =>
    buildVisibleSections(showCowork).map((s) => s.id),
  );
  const [hovered, setHovered] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const expanded = isMobile || hovered;
  const initialsColor = getInitialsColor(userInitials);

  useEffect(() => {
    setExpandedSections(visibleSections.map((s) => s.id));
  }, [visibleSections]);

  // Notify parent when expanded state changes
  useEffect(() => {
    if (!isMobile) {
      onExpandedChange?.(hovered);
    }
  }, [hovered, isMobile, onExpandedChange]);

  const handleMouseEnter = () => {
    if (isMobile) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHovered(true);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHovered(false);
      setShowSettingsMenu(false);
    }, 250);
  };

  useEffect(() => {
    if (!showSettingsMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        settingsMenuRef.current && !settingsMenuRef.current.contains(e.target as Node) &&
        settingsBtnRef.current && !settingsBtnRef.current.contains(e.target as Node)
      ) {
        setShowSettingsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsMenu]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setProfileImage(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(s => s !== sectionId)
        : [...prev, sectionId]
    );
  };

  const navigate = (page: string) => {
    onPageChange?.(page);
    onNavigate?.();
  };

  const _Avatar = ({ size = 40, className = '' }: { size?: number; className?: string }) => (
    <div
      className={cn('relative rounded-full flex-shrink-0 group/avatar cursor-pointer', className)}
      style={{ width: size, height: size }}
      onClick={() => fileInputRef.current?.click()}
      title={t('nav.uploadProfilePhoto')}
    >
      {profileImage ? (
        <img src={profileImage} alt={userName} className="w-full h-full rounded-full object-cover" />
      ) : (
        <div
          className="w-full h-full rounded-full flex items-center justify-center select-none"
          style={{ backgroundColor: initialsColor.bg, color: initialsColor.text, fontSize: size * 0.38 }}
        >
          <span style={{ fontWeight: 600, letterSpacing: '0.02em' }}>{userInitials}</span>
        </div>
      )}
      <div className="absolute inset-0 rounded-full bg-black/0 group-hover/avatar:bg-black/40 flex items-center justify-center transition-all">
        <Camera
          className="text-white opacity-0 group-hover/avatar:opacity-100 transition-opacity"
          style={{ width: size * 0.38, height: size * 0.38 }}
        />
      </div>
    </div>
  );

  return (
    <aside
      className={cn(
        'flex flex-col bg-white',
        isMobile ? 'w-full h-full' : 'fixed left-0 top-14 bottom-0 transition-all duration-300',
        !isMobile && (expanded ? 'w-64 z-50' : 'w-16 z-40')
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />

      {/* Scrollable navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-0.5">
        {/* Quick nav items */}
        {visibleQuickNav.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.page;
          return (
            <button
              key={item.label}
              onClick={() => navigate(item.page)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                isActive
                  ? 'bg-[#be43ff]/8 text-[#be43ff]'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              )}
              title={!expanded ? item.label : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {expanded && <span className="text-sm whitespace-nowrap">{item.label}</span>}
            </button>
          );
        })}

        {/* Divider */}
        <div className="!my-2.5 mx-1 border-t border-gray-100" />

        {/* Sections with sub-items */}
        {visibleSections.map((section) => {
          const visibleItems =
            section.id === 'Discover'
              ? section.items.filter((item) => !HIDDEN_DISCOVER_ITEM_LABELS.has(item.label))
              : section.items;
          const SectionIcon = section.icon;
          const isExpanded = expandedSections.includes(section.id);
          const isActiveSection = getActiveSectionId(currentPage || '', visibleSections) === section.id;
          const firstItemSlug = visibleItems.length > 0 ? toPageSlug(visibleItems[0].label) : 'dashboard';

          return (
            <div key={section.id}>
              {/* Section header */}
              <button
                onClick={() => {
                  if (expanded) {
                    toggleSection(section.id);
                  } else {
                    // When collapsed, clicking section icon navigates to first visible item
                    navigate(firstItemSlug);
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                  isActiveSection && !expanded
                    ? 'bg-[#be43ff]/8 text-[#be43ff]'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                )}
                title={!expanded ? section.label : undefined}
              >
                <SectionIcon className="w-5 h-5 flex-shrink-0" />
                {expanded && (
                  <>
                    <span className="text-xs uppercase tracking-wider flex-1 text-left whitespace-nowrap" style={{ fontWeight: 600 }}>
                      {section.label}
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-3.5 h-3.5 transition-transform shrink-0',
                        isExpanded ? '' : '-rotate-90'
                      )}
                    />
                  </>
                )}
              </button>

              {/* Sub-items (only when expanded sidebar + section open) */}
              {expanded && isExpanded && (
                <div className="mt-0.5 mb-1 space-y-0.5">
                  {visibleItems.map((item) => {
                    const ItemIcon = item.icon;
                    const page = toPageSlug(item.label);
                    const isActive = navPageForCreatorsSidebar(currentPage || '') === page;
                    return (
                      <button
                        key={item.label}
                        onClick={() => navigate(page)}
                        className={cn(
                          'w-full flex items-center gap-3 pl-11 pr-3 py-1.5 rounded-lg text-sm transition-colors',
                          isActive
                            ? 'bg-[#be43ff]/8 text-[#be43ff]'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                      >
                        <ItemIcon className={cn('w-4 h-4 flex-shrink-0', isActive ? 'text-[#be43ff]' : 'text-gray-400')} />
                        <span className="whitespace-nowrap">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Profile section – bottom - REMOVED (now in TopNavBar) */}
    </aside>
  );
}